import { DateTime } from "luxon";
import { Pressable, StyleSheet, View } from "react-native";

import { GText } from "@/components/g-text";
import { colors } from "@/constants/colors";

interface Props {
  displayMonth: DateTime;
  onNextMonth: () => void;
  onPrevMonth: () => void;
  onSelectDate: (date: DateTime) => void;
  selectedDate: DateTime | null;
}

const DAY_LABELS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export function CalendarGrid({
  displayMonth,
  onNextMonth,
  onPrevMonth,
  onSelectDate,
  selectedDate,
}: Props) {
  const today = DateTime.local().startOf("day");
  const days = buildCalendarGrid(displayMonth);

  return (
    <View>
      {/* Header */}
      <View style={styles.header}>
        <Pressable onPress={onPrevMonth} style={styles.arrow}>
          <GText style={styles.arrowText}>{"<"}</GText>
        </Pressable>
        <GText style={styles.monthLabel}>
          {displayMonth.toFormat("LLLL yyyy")}
        </GText>
        <Pressable onPress={onNextMonth} style={styles.arrow}>
          <GText style={styles.arrowText}>{">"}</GText>
        </Pressable>
      </View>

      {/* Day-of-week row */}
      <View style={styles.row}>
        {DAY_LABELS.map((d) => (
          <View key={d} style={styles.cell}>
            <GText style={styles.dowLabel}>{d}</GText>
          </View>
        ))}
      </View>

      {/* Day grid */}
      {Array.from({ length: 6 }, (_, week) => (
        <View key={week} style={styles.row}>
          {days.slice(week * 7, week * 7 + 7).map((day) => {
            const isCurrentMonth = day.month === displayMonth.month;
            const isSelected =
              selectedDate &&
              day.hasSame(selectedDate, "day") &&
              day.hasSame(selectedDate, "month") &&
              day.hasSame(selectedDate, "year");
            const isToday =
              day.hasSame(today, "day") &&
              day.hasSame(today, "month") &&
              day.hasSame(today, "year");
            const isPast = day < today;

            return (
              <Pressable
                key={day.toISODate()}
                onPress={() => !isPast && onSelectDate(day)}
                style={styles.cell}
              >
                <View
                  style={[
                    styles.dayCircle,
                    isSelected && styles.dayCircleSelected,
                  ]}
                >
                  <GText
                    style={[
                      styles.dayText,
                      (!isCurrentMonth || isPast) && styles.dayTextOutside,
                      isSelected && styles.dayTextSelected,
                    ]}
                  >
                    {day.day}
                  </GText>
                </View>
                {isToday && !isSelected && <View style={styles.todayDot} />}
              </Pressable>
            );
          })}
        </View>
      ))}
    </View>
  );
}

function buildCalendarGrid(displayMonth: DateTime): DateTime[] {
  const firstOfMonth = displayMonth.startOf("month");
  // Sunday = 0 in JS convention; Luxon weekday: 1=Mon..7=Sun
  const startDow = firstOfMonth.weekday % 7; // Sun=0, Mon=1, ...
  const gridStart = firstOfMonth.minus({ days: startDow });

  const days: DateTime[] = [];
  for (let i = 0; i < 42; i++) {
    days.push(gridStart.plus({ days: i }));
  }
  return days;
}

const CELL_SIZE = 40;

const styles = StyleSheet.create({
  arrow: {
    padding: 8,
  },
  arrowText: {
    color: colors.white,
    fontSize: 20,
    fontWeight: "600",
  },
  cell: {
    alignItems: "center",
    height: CELL_SIZE,
    justifyContent: "center",
    width: CELL_SIZE,
  },
  dayCircle: {
    alignItems: "center",
    borderRadius: 16,
    height: 32,
    justifyContent: "center",
    width: 32,
  },
  dayCircleSelected: {
    backgroundColor: colors.white,
  },
  dayText: {
    color: colors.white,
    fontSize: 14,
  },
  dayTextOutside: {
    color: colors.subtle,
  },
  dayTextSelected: {
    color: colors.black,
    fontWeight: "600",
  },
  dowLabel: {
    color: colors.subtle,
    fontSize: 12,
    fontWeight: "600",
  },
  header: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  monthLabel: {
    color: colors.white,
    fontSize: 17,
    fontWeight: "600",
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-around",
  },
  todayDot: {
    backgroundColor: colors.white,
    borderRadius: 1,
    height: 2,
    marginTop: 1,
    width: 16,
  },
});
